// G10 — Seção "Cota do Assistente" no /admin/usuarios/<id>/editar.
//
// Espelha o UserQuotaSection (Hub) mas no endpoint próprio do Assistente
// (`/admin/users/<id>/general-quota/`). 2 abas: "Editar" (contadores + limites +
// modelos + reset) e "Auditoria" (GeneralAgentQuotaChangeLog). O `reason` opcional
// vai pro audit via signal pre_save.

import { FC, useCallback, useEffect, useState } from 'react';
import assistantQuotaService from '@/services/assistantQuota.service';
import { useToast } from '@/contexts/ToastContext';
import { AdminGeneralQuota, GeneralQuotaChangeLog, UpdateGeneralQuotaPayload } from '@/types/assistant';

type TabId = 'edit' | 'audit';

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtPct(used: number, max: number): string {
  if (!max) return '0%';
  return `${Math.round((used / max) * 100)}%`;
}

function UsageBar({ label, used, max }: { label: string; used: number; max: number }) {
  const pct = max ? Math.min(100, (used / max) * 100) : 0;
  const danger = pct >= 90;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-content-tertiary">{label}</span>
        <span className={`font-mono ${danger ? 'text-red-300' : 'text-content-primary'}`}>
          {used.toLocaleString('pt-BR')} / {max.toLocaleString('pt-BR')} ({fmtPct(used, max)})
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-border overflow-hidden">
        <div
          className={`h-full transition-all ${danger ? 'bg-red-400' : 'bg-accent'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  testId: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-content-primary block mb-1">{label}</span>
      <input
        type="number"
        min={0}
        value={value ?? 0}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
        data-testid={testId}
        className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
      />
    </label>
  );
}

interface Props {
  userId: string;
}

const AssistantQuotaSection: FC<Props> = ({ userId }) => {
  const { showToast } = useToast();
  const [quota, setQuota] = useState<AdminGeneralQuota | null>(null);
  const [form, setForm] = useState<Partial<AdminGeneralQuota> | null>(null);
  const [reason, setReason] = useState('');
  const [audit, setAudit] = useState<GeneralQuotaChangeLog[]>([]);
  const [tab, setTab] = useState<TabId>('edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [q, a] = await Promise.all([
        assistantQuotaService.getUserQuota(userId),
        assistantQuotaService.getUserQuotaAudit(userId).catch(() => []),
      ]);
      setQuota(q);
      setForm(q);
      setAudit(a);
    } catch {
      showToast('Não foi possível carregar a cota do Assistente.', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload: UpdateGeneralQuotaPayload = {
        max_tokens_per_day: form.max_tokens_per_day,
        max_tokens_per_month: form.max_tokens_per_month,
        max_files_per_day: form.max_files_per_day,
        max_uploads_per_day: form.max_uploads_per_day,
        max_web_searches_per_day: form.max_web_searches_per_day,
        allowed_models: form.allowed_models,
        ...(reason ? { reason } : {}),
      };
      await assistantQuotaService.updateUserQuota(userId, payload);
      showToast('Cota do Assistente atualizada.', 'success');
      setReason('');
      await refresh();
    } catch {
      showToast('Erro ao salvar.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetCounters = async () => {
    if (!window.confirm('Zerar contadores do Assistente agora? Não pode ser desfeito.')) return;
    try {
      await assistantQuotaService.resetUserQuotaCounters(userId);
      showToast('Contadores zerados.', 'success');
      await refresh();
    } catch {
      showToast('Erro ao zerar.', 'error');
    }
  };

  if (loading) {
    return (
      <section
        className="border-t border-surface-border pt-6 mt-6"
        data-testid="assistant-quota-section"
      >
        <p className="text-sm text-content-tertiary">Carregando cota do Assistente…</p>
      </section>
    );
  }

  if (!quota || !form) return null;

  const catalogModels = Object.keys(quota.available_models_catalog ?? {});

  return (
    <section
      className="border-t border-surface-border pt-6 mt-6 space-y-4"
      data-testid="assistant-quota-section"
    >
      <h2 className="text-lg font-semibold text-content-primary flex items-center gap-2">
        <span className="material-symbols-outlined text-accent">auto_awesome</span>
        Cota do Assistente (H.Bot)
      </h2>

      <div className="flex border-b border-surface-border">
        <button
          type="button"
          onClick={() => setTab('edit')}
          data-testid="assistant-quota-tab-edit"
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            tab === 'edit'
              ? 'border-accent text-accent'
              : 'border-transparent text-content-tertiary hover:text-content-primary'
          }`}
        >
          Editar
        </button>
        <button
          type="button"
          onClick={() => setTab('audit')}
          data-testid="assistant-quota-tab-audit"
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            tab === 'audit'
              ? 'border-accent text-accent'
              : 'border-transparent text-content-tertiary hover:text-content-primary'
          }`}
        >
          Auditoria ({audit.length})
        </button>
      </div>

      {tab === 'edit' && (
        <div className="space-y-4">
          <div className="space-y-2" data-testid="assistant-quota-usage">
            <UsageBar label="Tokens hoje" used={quota.tokens_used_today} max={quota.max_tokens_per_day} />
            <UsageBar label="Tokens este mês" used={quota.tokens_used_month} max={quota.max_tokens_per_month} />
            <UsageBar label="Arquivos hoje" used={quota.files_created_today} max={quota.max_files_per_day} />
            <UsageBar label="Uploads hoje" used={quota.uploads_today} max={quota.max_uploads_per_day} />
            <UsageBar
              label="Buscas web hoje"
              used={quota.web_searches_today}
              max={quota.max_web_searches_per_day}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NumberInput
              label="Tokens / dia"
              value={form.max_tokens_per_day}
              onChange={(v) => setForm({ ...form, max_tokens_per_day: v })}
              testId="ag-tokens-day"
            />
            <NumberInput
              label="Tokens / mês"
              value={form.max_tokens_per_month}
              onChange={(v) => setForm({ ...form, max_tokens_per_month: v })}
              testId="ag-tokens-month"
            />
            <NumberInput
              label="Arquivos / dia"
              value={form.max_files_per_day}
              onChange={(v) => setForm({ ...form, max_files_per_day: v })}
              testId="ag-files-day"
            />
            <NumberInput
              label="Uploads / dia"
              value={form.max_uploads_per_day}
              onChange={(v) => setForm({ ...form, max_uploads_per_day: v })}
              testId="ag-uploads-day"
            />
            <NumberInput
              label="Buscas web / dia"
              value={form.max_web_searches_per_day}
              onChange={(v) => setForm({ ...form, max_web_searches_per_day: v })}
              testId="ag-web-day"
            />
          </div>

          <div>
            <span className="text-xs font-semibold text-content-primary block mb-1">
              Modelos permitidos
            </span>
            {catalogModels.length === 0 ? (
              <p className="text-xs text-content-tertiary">Catálogo indisponível.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="ag-models-multiselect">
                {catalogModels.map((m) => {
                  const checked = (form.allowed_models ?? []).includes(m);
                  return (
                    <label
                      key={m}
                      className={`px-3 py-1.5 rounded-lg border cursor-pointer text-sm select-none ${
                        checked
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-surface-border text-content-tertiary hover:text-content-primary'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => {
                          const cur = new Set(form.allowed_models ?? []);
                          if (checked) cur.delete(m);
                          else cur.add(m);
                          setForm({ ...form, allowed_models: Array.from(cur) });
                        }}
                      />
                      {m}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-content-primary block mb-1">
              Motivo da alteração (opcional)
            </span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="assistant-quota-reason"
              placeholder="Ex: Aprovado pelo gerente em ticket #123."
              className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
            />
            <p className="text-xs text-content-tertiary mt-1">
              Vai pro log de auditoria. Use pra contextualizar mudanças.
            </p>
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="assistant-quota-save"
              className="bg-accent hover:bg-accent-hover text-accent-contrast font-medium px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            >
              {saving ? 'Salvando…' : 'Salvar cota'}
            </button>
            <button
              type="button"
              onClick={handleResetCounters}
              data-testid="assistant-quota-reset"
              className="border border-surface-border text-content-primary px-4 py-2 rounded-lg hover:bg-surface-secondary"
            >
              Zerar contadores agora
            </button>
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <ul className="space-y-2 text-sm" data-testid="assistant-audit-list">
          {audit.length === 0 ? (
            <p className="text-content-tertiary">Sem mudanças registradas.</p>
          ) : (
            audit.map((log) => (
              <li
                key={log.id}
                className="border-l-4 border-accent pl-3 py-1 rounded-r bg-surface-secondary"
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <strong className="text-content-primary">{log.admin_user_name ?? 'Sistema'}</strong>
                  <span className="text-content-tertiary text-xs">mudou</span>
                  <code className="text-xs bg-surface-primary px-1.5 py-0.5 rounded text-accent">
                    {log.field_changed}
                  </code>
                  <span className="text-content-tertiary text-xs">de</span>
                  <code className="text-xs bg-surface-primary px-1.5 py-0.5 rounded">
                    {JSON.stringify(log.old_value)}
                  </code>
                  <span className="text-content-tertiary text-xs">pra</span>
                  <code className="text-xs bg-surface-primary px-1.5 py-0.5 rounded">
                    {JSON.stringify(log.new_value)}
                  </code>
                  <span className="text-content-tertiary text-xs ml-auto">
                    {fmtDate(log.created_at)}
                  </span>
                </div>
                {log.reason && (
                  <p className="text-content-tertiary italic text-xs mt-1">"{log.reason}"</p>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </section>
  );
};

export default AssistantQuotaSection;
